import React, { useMemo, useState, useEffect } from "react";
import {
  useTable,
  useGlobalFilter,
  useFilters,
  useSortBy,
  usePagination,
} from "react-table";

import { COLUMNS } from "./helpers/Columns";
import { SubmissionModal } from "./SubmissionModal";
import { ReactComponent as NoTickets } from "images/svg/empty-tickets.svg";
import { ReactComponent as PencilIcon } from "images/svg/pencil.svg";
import { FadeLoader } from "react-spinners";
import "../styles.scss";

const SupportTicketsTable = ({
  tickets,
  metadata,
  isLoading,
}) => {
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [data, setData] = useState([]);
  const [entriesPerPage, setEntriesPerPage] = useState(3);

  useEffect(() => {
    setData(tickets);
  }, [isLoading, tickets]);

  const columns = useMemo(
    () => COLUMNS(),
    []
  );

  const tableInstance = useTable(
    { 
      columns, 
      data, 
      initialState: { 
        pageIndex: 0,
        pageSize: entriesPerPage 
      },
      pageSize: entriesPerPage,
      manualPagination: false
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const { 
    getTableProps, 
    getTableBodyProps, 
    headerGroups, 
    page, 
    prepareRow,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex }
  } = tableInstance;

  useEffect(() => {
    setPageSize(entriesPerPage);
  }, [entriesPerPage, setPageSize]);

  const handleEntriesPerPageChange = (e) => {
    const value = Number(e.target.value);
    setEntriesPerPage(value);
    gotoPage(0);
  };

  const renderPaginationButtons = () => {
    const totalPages = pageCount > 0 ? pageCount : 1;
    const buttons = [];
    
    buttons.push(
      <button key="first" onClick={() => gotoPage(0)} className="pagination-button">«</button>,
      <button key="prev" onClick={() => previousPage()} className="pagination-button">‹</button>
    );
    
    for (let i = 1; i <= Math.min(8, totalPages); i++) {
      buttons.push(
        <button 
          key={i} 
          onClick={() => gotoPage(i-1)}
          className={`pagination-button ${pageIndex === i-1 ? 'active' : ''}`}
        >
          {i}
        </button>
      );
    }
    
    if (totalPages > 8) {
      buttons.push(
        <span key="ellipsis" className="pagination-ellipsis">...</span>,
        <button key="100" className="pagination-button">100</button>
      );
    }
    
    buttons.push(
      <button key="next" onClick={() => nextPage()} className="pagination-button">›</button>,
      <button key="last" onClick={() => gotoPage(pageCount - 1)} className="pagination-button">»</button>
    );
    
    return buttons;
  };

  const getStatusClass = (status) => {
    return status.toLowerCase() === 'open' ? 'status-open' : 'status-closed';
  };

  return (
    <div>
      <SubmissionModal
        setShowSubmissionModal={setShowSubmissionModal}
        handleCloseSubmissionModal={() => setShowSubmissionModal(false)}
        showSubmissionModal={showSubmissionModal}
      />

      <div className={`support-tickets-card ${isLoading ? "disabled" : ""}`}>
        {isLoading ? (
          <div className="loading-container">
            <FadeLoader color="#36d7b7" />
          </div>
        ) : (
          <>
            {tickets?.length > 0 ? ( 
              <>
              <div className="table-responsive">
                <table
                  {...getTableProps()}
                  className="support-table"
                >
                  <thead>
                    {headerGroups.map((headerGroup) => (
                      <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => (
                          <th
                            {...column.getHeaderProps(column.getSortByToggleProps())}
                            className="support-header-cell"
                          >
                            {column.render("Header")}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...getTableBodyProps()}>
                    {page.map((row) => {
                      prepareRow(row);
                      return (
                        <tr {...row.getRowProps()} className="support-row">
                          {row.cells.map((cell) => {
                            if (cell.column.id === "status") {
                              return (
                                <td {...cell.getCellProps()} className="support-cell">
                                  <span className={`status-badge ${getStatusClass(cell.value)}`}>
                                    {cell.render("Cell")}
                                  </span>
                                </td>
                              );
                            } else {
                              return (
                                <td {...cell.getCellProps()} className="support-cell">
                                  {cell.render("Cell")}
                                </td>
                              );
                            }
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="support-pagination-container">
                  <div className="entries-selector">
                    <span>Show</span>
                    <select 
                      value={entriesPerPage}
                      onChange={handleEntriesPerPageChange}
                    >
                      <option value={3}>3</option>
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                    </select>
                    <span>Entries</span>
                  </div>
                  
                  <div className="pagination-buttons">
                    {renderPaginationButtons()}
                  </div>
                </div>
              </div>
            </>
            ) : (
              <div className="empty-support">
                <NoTickets />
                <h4 className="my-2">
                  No tickets Available
                </h4>
                <p>Do add a ticket when you run into roadblocks</p>
                <button
                  type="button"
                  onClick={() => setShowSubmissionModal(true)}
                >
                  <PencilIcon className="mr-2" />
                  Report an Issue
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SupportTicketsTable;